import { lookupAirport, resolveCoords } from "./airports";
import { formatMoney, type CurrencyCode, type MoneyCatalogs } from "./catalogs";
import { haversineNm } from "./geo";
import { FLIGHT_TRACKS_MAX } from "./mapMemory";
import { type CharterQuote } from "./ledger";
import { buildTripPnl } from "./tripPnl";
import type { FusedPosition, PositionSample, PositionSource } from "./position";
import type {
  AircraftSpec,
  CompanyAccount,
  FlightLogEntry,
  LedgerTxn,
  Mission,
  RateTables,
  SceneryAirport,
} from "./types";

export type TrackPoint = {
  lat: number;
  lon: number;
  altFt?: number;
  heading?: number;
  atUtc: string;
  source: PositionSource | "manual";
};

export type FlightTrack = {
  id: string;
  flightLogId: string;
  tripId: string;
  points: TrackPoint[];
  manualNotes: string;
};

export const AAR_SPARSE_POINTS = 3;
export const TRACK_MIN_INTERVAL_MS = 12_000;
export const TRACK_MIN_STEP_NM = 0.35;
export const TRACK_MAX_POINTS = 400;

export function newTrackId(now = new Date()): string {
  return `trk-${now.getTime().toString(36)}-${Math.floor(Math.random() * 1e4).toString(36)}`;
}

export function emptyFlightTrack(input: { flightLogId: string; tripId: string; now?: Date }): FlightTrack {
  return {
    id: newTrackId(input.now),
    flightLogId: input.flightLogId,
    tripId: input.tripId,
    points: [],
    manualNotes: "",
  };
}

function validPoint(p: Partial<TrackPoint> | null | undefined): p is TrackPoint {
  return Boolean(
    p &&
      typeof p.lat === "number" &&
      typeof p.lon === "number" &&
      Number.isFinite(p.lat) &&
      Number.isFinite(p.lon) &&
      Math.abs(p.lat) <= 90 &&
      Math.abs(p.lon) <= 180,
  );
}

export function hydrateTrackPoint(raw: unknown): TrackPoint | null {
  if (!raw || typeof raw !== "object") return null;
  const row = raw as Record<string, unknown>;
  const point: TrackPoint = {
    lat: Number(row.lat),
    lon: Number(row.lon),
    altFt: typeof row.altFt === "number" ? row.altFt : undefined,
    heading: typeof row.heading === "number" ? row.heading : undefined,
    atUtc: typeof row.atUtc === "string" ? row.atUtc : new Date().toISOString(),
    source: row.source === "simconnect" || row.source === "sayintentions" || row.source === "vatsim" || row.source === "last_known" || row.source === "manual"
      ? row.source
      : "manual",
  };
  return validPoint(point) ? point : null;
}

export function hydrateFlightTrack(raw?: Partial<FlightTrack> | null): FlightTrack | null {
  if (!raw || typeof raw.flightLogId !== "string" || typeof raw.tripId !== "string") return null;
  const points = Array.isArray(raw.points)
    ? raw.points.map(hydrateTrackPoint).filter((p): p is TrackPoint => Boolean(p)).slice(-TRACK_MAX_POINTS)
    : [];
  return {
    id: typeof raw.id === "string" && raw.id ? raw.id : newTrackId(),
    flightLogId: raw.flightLogId,
    tripId: raw.tripId,
    points,
    manualNotes: typeof raw.manualNotes === "string" ? raw.manualNotes : "",
  };
}

export function hydrateFlightTracks(raw?: unknown): FlightTrack[] {
  if (!Array.isArray(raw)) return [];
  return raw
    .map((row) => hydrateFlightTrack(row as Partial<FlightTrack>))
    .filter((t): t is FlightTrack => Boolean(t))
    .slice(0, FLIGHT_TRACKS_MAX);
}

export function pointFromPosition(pos: PositionSample | FusedPosition, source?: PositionSource | "manual"): TrackPoint {
  const src: PositionSource | "manual" =
    source ?? ("source" in pos && pos.source ? pos.source : "manual");
  return {
    lat: pos.lat,
    lon: pos.lon,
    altFt: pos.altitudeFt ?? undefined,
    heading: pos.heading ?? undefined,
    atUtc: pos.atUtc,
    source: src,
  };
}

export function shouldAppendTrackPoint(prev: TrackPoint | undefined, next: TrackPoint): boolean {
  if (!prev) return true;
  const dt = new Date(next.atUtc).getTime() - new Date(prev.atUtc).getTime();
  if (Number.isFinite(dt) && dt >= TRACK_MIN_INTERVAL_MS) return true;
  return haversineNm(prev.lat, prev.lon, next.lat, next.lon) >= TRACK_MIN_STEP_NM;
}

export function appendTrackPoint(track: FlightTrack, point: TrackPoint): FlightTrack {
  if (!validPoint(point)) return track;
  const last = track.points[track.points.length - 1];
  if (!shouldAppendTrackPoint(last, point)) return track;
  const points = [...track.points, point].slice(-TRACK_MAX_POINTS);
  return { ...track, points };
}

export function addManualWaypoint(
  track: FlightTrack,
  input: { lat?: number; lon?: number; icao?: string; scenery?: SceneryAirport[]; notes?: string; now?: Date },
): { ok: true; track: FlightTrack } | { ok: false; error: string } {
  let lat = input.lat;
  let lon = input.lon;
  if ((lat == null || lon == null) && input.icao) {
    const c = resolveCoords(input.icao, input.scenery ?? []) ?? (lookupAirport(input.icao)
      ? { lat: lookupAirport(input.icao)!.lat, lon: lookupAirport(input.icao)!.lon }
      : null);
    if (!c) return { ok: false, error: `No coordinates for ${input.icao.toUpperCase()}.` };
    lat = c.lat;
    lon = c.lon;
  }
  if (lat == null || lon == null) return { ok: false, error: "Enter ICAO or lat/lon." };
  const now = input.now ?? new Date();
  const next = appendTrackPoint(track, {
    lat,
    lon,
    atUtc: now.toISOString(),
    source: "manual",
  });
  return {
    ok: true,
    track: {
      ...next,
      manualNotes: input.notes?.trim() ? input.notes.trim() : track.manualNotes,
    },
  };
}

export function isTrackSparse(track: FlightTrack | null | undefined): boolean {
  return !track || track.points.length < AAR_SPARSE_POINTS;
}

export function trackDistanceNm(track: FlightTrack | null | undefined): number {
  if (!track || track.points.length < 2) return 0;
  let sum = 0;
  for (let i = 1; i < track.points.length; i += 1) {
    const a = track.points[i - 1]!;
    const b = track.points[i]!;
    sum += haversineNm(a.lat, a.lon, b.lat, b.lon);
  }
  return Math.round(sum * 10) / 10;
}

export function findTrackForLog(tracks: FlightTrack[], log: Pick<FlightLogEntry, "id" | "tripId">): FlightTrack | undefined {
  return tracks.find((t) => t.flightLogId === log.id || t.tripId === log.tripId || t.tripId === log.id);
}

export function upsertTrackList(list: FlightTrack[], track: FlightTrack): FlightTrack[] {
  const idx = list.findIndex((t) => t.id === track.id || t.flightLogId === track.flightLogId || t.tripId === track.tripId);
  const next = idx === -1 ? [track, ...list] : list.map((t, i) => (i === idx ? track : t));
  return next.length > FLIGHT_TRACKS_MAX ? next.slice(0, FLIGHT_TRACKS_MAX) : next;
}

function trackKey(track: Pick<FlightTrack, "tripId" | "flightLogId" | "id">): string {
  return track.tripId || track.flightLogId || track.id;
}

function trackRecency(track: FlightTrack): string {
  return track.points[track.points.length - 1]?.atUtc ?? "";
}

function sameTrackPoints(a: TrackPoint[], b: TrackPoint[]): boolean {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i += 1) {
    const left = a[i]!;
    const right = b[i]!;
    if (left.atUtc !== right.atUtc || left.lat !== right.lat || left.lon !== right.lon || left.altFt !== right.altFt) {
      return false;
    }
  }
  return true;
}

/** Union by time. A shorter remote list must not drop local history. */
export function mergeTrackPoints(local: readonly TrackPoint[], incoming: readonly TrackPoint[]): TrackPoint[] {
  const seen = new Set<string>();
  const out: TrackPoint[] = [];
  for (const point of [...local, ...incoming]) {
    if (!validPoint(point)) continue;
    const key = `${point.atUtc}|${point.lat.toFixed(4)}|${point.lon.toFixed(4)}`;
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(point);
  }
  out.sort((a, b) => (a.atUtc < b.atUtc ? -1 : a.atUtc > b.atUtc ? 1 : 0));
  return out.length > TRACK_MAX_POINTS ? out.slice(-TRACK_MAX_POINTS) : out;
}

/**
 * One trail per ticket. Empty incoming keeps local. Overlapping points union
 * so a pull that only has the current fix cannot restart the line.
 */
export function mergeFlightTracks(
  local: readonly FlightTrack[] | null | undefined,
  incoming: readonly FlightTrack[] | null | undefined,
): FlightTrack[] {
  const extra = Array.isArray(incoming) ? incoming : [];
  const kept = Array.isArray(local) ? local : [];
  // Empty incoming keeps the same array so a pull cannot restart the trail.
  if (!extra.length) return kept as FlightTrack[];
  const map = new Map<string, FlightTrack>();
  for (const track of kept) {
    const key = trackKey(track);
    if (key) map.set(key, track);
  }
  let changed = false;
  for (const track of extra) {
    const key = trackKey(track);
    if (!key) continue;
    const prev = map.get(key);
    if (!prev) {
      map.set(key, track);
      changed = true;
      continue;
    }
    const points = mergeTrackPoints(prev.points, track.points);
    if (sameTrackPoints(prev.points, points)) continue;
    changed = true;
    map.set(key, {
      ...prev,
      id: prev.points.length >= track.points.length ? prev.id : track.id || prev.id,
      flightLogId: prev.flightLogId || track.flightLogId,
      tripId: prev.tripId || track.tripId,
      points,
      manualNotes: prev.manualNotes || track.manualNotes,
    });
  }
  if (!changed) return kept as FlightTrack[];
  const next = [...map.values()].sort((a, b) => (trackRecency(a) < trackRecency(b) ? 1 : trackRecency(a) > trackRecency(b) ? -1 : 0));
  return next.length > FLIGHT_TRACKS_MAX ? next.slice(0, FLIGHT_TRACKS_MAX) : next;
}

/** Sim / tracker samples extend the trail. A last-known hold must not eat the cap. */
export function shouldSampleLiveTrail(source: string | null | undefined): boolean {
  return source === "simconnect" || source === "sayintentions" || source === "vatsim" || source === "manual";
}

export function liveTrailBindings(input: {
  logs?: ReadonlyArray<{ id: string; tripId: string; status: string }> | null;
  missionId?: string | null;
  /** Open telemetry session. The sample belongs to this trip, not every filed log. */
  session?: { tripId?: string | null; flightLogId?: string | null } | null;
}): Array<{ flightLogId: string; tripId: string }> {
  const sessionTrip = input.session?.tripId?.trim() ?? "";
  const sessionLog = input.session?.flightLogId?.trim() ?? "";
  const missionId = input.missionId?.trim() ?? "";
  const ownTrip = sessionTrip || missionId;
  const owns = (log: { id: string; tripId: string }) => {
    if (sessionLog && (log.id === sessionLog || log.tripId === sessionLog)) return true;
    if (ownTrip && (log.id === ownTrip || log.tripId === ownTrip)) return true;
    return false;
  };
  for (const log of input.logs ?? []) {
    if (log.status !== "filed" && log.status !== "airborne") continue;
    if (!owns(log)) continue;
    const tripId = log.tripId || log.id;
    const flightLogId = log.id || tripId;
    if (!tripId) continue;
    return [{ flightLogId, tripId }];
  }
  const fallbackTrip = ownTrip || sessionLog;
  if (!fallbackTrip) return [];
  return [{ flightLogId: sessionLog || fallbackTrip, tripId: fallbackTrip }];
}

/** Append one sample onto each bound ticket. Same array when nothing new landed. */
export function appendLiveTrail(
  tracks: FlightTrack[],
  point: TrackPoint,
  bindings: ReadonlyArray<{ flightLogId: string; tripId: string }>,
): FlightTrack[] {
  if (!bindings.length || !validPoint(point)) return tracks;
  let next = tracks;
  for (const binding of bindings) {
    const tripId = binding.tripId || binding.flightLogId;
    const flightLogId = binding.flightLogId || tripId;
    if (!tripId) continue;
    const existing =
      findTrackForLog(next, { id: flightLogId, tripId }) ?? emptyFlightTrack({ flightLogId, tripId });
    const updated = appendTrackPoint(existing, point);
    if (updated !== existing) next = upsertTrackList(next, updated);
  }
  return next;
}

export function latestFlightTrack(tracks: readonly FlightTrack[] | null | undefined): FlightTrack | null {
  let best: FlightTrack | null = null;
  let bestAt = "";
  for (const track of tracks ?? []) {
    if (!track.points.length) continue;
    const at = track.points[track.points.length - 1]?.atUtc ?? "";
    if (!best || at >= bestAt) {
      best = track;
      bestAt = at;
    }
  }
  return best;
}

/** Compact identity for the live channel — not the coordinate list. */
export function flightTrackFingerprint(track: FlightTrack | null | undefined): string {
  if (!track?.points.length) return "";
  const last = track.points[track.points.length - 1]!;
  return `${track.tripId}|${track.points.length}|${last.atUtc}`;
}

export type AarPnlLine = {
  kind: string;
  memo: string;
  amountCad: number;
};

export type AarPnl = {
  revenueCad: number;
  costsCad: number;
  netCad: number;
  lines: AarPnlLine[];
  source: "ledger" | "catalog_estimate";
  currency: CurrencyCode;
};

export function ledgerLinesForTrip(account: CompanyAccount | undefined, tripId: string): LedgerTxn[] {
  if (!account) return [];
  return account.transactions.filter((t) => t.missionId === tripId);
}

export function pnlFromLines(lines: AarPnlLine[], currency: CurrencyCode, source: AarPnl["source"]): AarPnl {
  const revenueCad = Math.round(lines.filter((l) => l.amountCad > 0).reduce((s, l) => s + l.amountCad, 0) * 100) / 100;
  const costsCad = Math.round(lines.filter((l) => l.amountCad < 0).reduce((s, l) => s + l.amountCad, 0) * 100) / 100;
  return {
    revenueCad,
    costsCad,
    netCad: Math.round((revenueCad + costsCad) * 100) / 100,
    lines,
    source,
    currency,
  };
}

export function estimatePnlFromQuote(quote: CharterQuote, currency: CurrencyCode, route: string): AarPnl {
  const lines: AarPnlLine[] = [];
  if (quote.revenueCad > 0) {
    lines.push({ kind: "charter_revenue", memo: `Rate-card charter ${route}`, amountCad: quote.revenueCad });
  }
  if (quote.fuelCad > 0) lines.push({ kind: "fuel", memo: `Fuel ${quote.fuelIcao}`, amountCad: -quote.fuelCad });
  if (quote.handlingCad > 0) {
    lines.push({ kind: "handling", memo: `Handling ${quote.opsIcao}`, amountCad: -quote.handlingCad });
  }
  if (quote.landingCad > 0) {
    lines.push({ kind: "landing", memo: `Landing ${quote.opsIcao}`, amountCad: -quote.landingCad });
  }
  if (quote.parkingCad > 0) {
    lines.push({ kind: "parking", memo: `Parking ${quote.opsIcao}`, amountCad: -quote.parkingCad });
  }
  if (quote.mxReserveCad > 0) {
    lines.push({ kind: "mx_reserve", memo: "Mx reserve", amountCad: -quote.mxReserveCad });
  }
  if (quote.engineReserveCad > 0) {
    lines.push({ kind: "engine_reserve", memo: "Engine reserve", amountCad: -quote.engineReserveCad });
  }
  return pnlFromLines(lines, currency, "catalog_estimate");
}

export function buildAarPnl(input: {
  mission?: Mission;
  log?: FlightLogEntry;
  account?: CompanyAccount;
  rates: RateTables;
  catalogs?: MoneyCatalogs;
  aircraft?: AircraftSpec;
  currency?: CurrencyCode;
  missions?: Mission[];
  logs?: FlightLogEntry[];
  fleet?: AircraftSpec[];
}): AarPnl {
  const currency = input.currency ?? input.catalogs?.currency ?? input.account?.homeCurrency ?? "CAD";
  if (input.mission) {
    const trip = buildTripPnl({
      mission: input.mission,
      missions: input.missions,
      logs: input.logs ?? (input.log ? [input.log] : undefined),
      account: input.account,
      rates: input.rates,
      catalogs: input.catalogs,
      fleet: input.fleet ?? (input.aircraft ? [input.aircraft] : undefined),
      currency,
    });
    return {
      revenueCad: trip.revenue.amountCad,
      costsCad: trip.costsCad,
      netCad: trip.netCad,
      lines: trip.lines.map((l) => ({ kind: l.kind, memo: `${l.memo} (${l.basis})`, amountCad: l.amountCad })),
      source: trip.posted ? "ledger" : "catalog_estimate",
      currency: trip.currency,
    };
  }
  const tripId = input.log?.tripId ?? "";
  const posted = tripId ? ledgerLinesForTrip(input.account, tripId) : [];
  if (posted.length) {
    return pnlFromLines(
      posted.map((t) => ({ kind: t.kind, memo: t.memo, amountCad: t.amountCad })),
      currency,
      "ledger",
    );
  }
  return pnlFromLines([], currency, "catalog_estimate");
}

export type AarReport = {
  log?: FlightLogEntry;
  mission?: Mission;
  track: FlightTrack;
  sparse: boolean;
  trackNm: number;
  pnl: AarPnl;
};

export function buildAarReport(input: {
  log?: FlightLogEntry;
  mission?: Mission;
  track?: FlightTrack | null;
  account?: CompanyAccount;
  rates: RateTables;
  catalogs?: MoneyCatalogs;
  aircraft?: AircraftSpec;
  currency?: CurrencyCode;
  missions?: Mission[];
  logs?: FlightLogEntry[];
  fleet?: AircraftSpec[];
}): AarReport {
  const track =
    input.track ??
    emptyFlightTrack({
      flightLogId: input.log?.id ?? "",
      tripId: input.mission?.id ?? input.log?.tripId ?? "",
    });
  return {
    log: input.log,
    mission: input.mission,
    track,
    sparse: isTrackSparse(track),
    trackNm: trackDistanceNm(track),
    pnl: buildAarPnl(input),
  };
}

export function formatAarMoney(amount: number, currency: CurrencyCode = "CAD"): string {
  return formatMoney(amount, currency);
}
